import { useEffect, useMemo, useState } from 'react';
import { useRows } from '@/hooks/useLive';
import { save, softDelete, upsert } from '@/lib/crud';
import { num, isoDate } from '@/lib/format';
import { Drawer } from '@/components/ui/Drawer';
import { Button } from '@/components/ui/Button';
import { ConfirmRemove } from '@/components/ui/ConfirmRemove';
import { StatusChip } from '@/components/ui/StatusChip';
import { ragTone } from '@/components/ui/statusTone';
import {
  NumberInput, SelectInput, TextArea, TextInput,
} from '@/components/ui/Field';
import type { Machine } from '@/types/models';
import type { PartStock } from '../Parts';

interface Breakdown extends Record<string, unknown> {
  id: string;
  breakdown_no: string;
  machine_id: string;
  status: string;
}

/** Which direction each movement type takes on the ledger. The database
 *  enforces the same rule; the form applies it so nobody has to type a
 *  minus sign to issue a filter. */
const TYPES = [
  { value: 'receipt', label: 'Receipt — stock in', sign: 1 },
  { value: 'issue', label: 'Issue — to a machine or job', sign: -1 },
  { value: 'return', label: 'Return — unused stock back', sign: 1 },
  { value: 'opening', label: 'Opening balance', sign: 1 },
  { value: 'scrap', label: 'Scrap — written off', sign: -1 },
  { value: 'adjustment', label: 'Adjustment — stock count correction', sign: 0 },
];

export function MovementDrawer({
  record, stock, onClose, locked,
}: { record: Record<string, unknown>; stock: PartStock[]; onClose: () => void; locked?: string | null }) {
  const isNew = !record.id;
  const parts = useRows<Record<string, unknown>>('parts', 'part_no');
  const machines = useRows<Machine>('machines', 'fleet_no');
  const breakdowns = useRows<Breakdown>('breakdowns', 'breakdown_date');

  const [v, setV] = useState<Record<string, unknown>>({});
  const [qty, setQty] = useState<number | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setV({
      movement_date: isoDate(),
      movement_type: 'receipt',
      ...record,
    });
    // An adjustment is the only signed entry — every other type is captured
    // as a plain quantity and given its direction on save.
    const delta = record.qty_delta == null ? null : Number(record.qty_delta);
    setQty(delta == null ? null : record.movement_type === 'adjustment' ? delta : Math.abs(delta));
  }, [record]);

  const set = (k: string, val: unknown) => setV((d) => ({ ...d, [k]: val }));

  const type = TYPES.find((t) => t.value === v.movement_type) ?? TYPES[0];
  const isAdjustment = type.value === 'adjustment';
  const delta = qty == null ? null : isAdjustment ? qty : Math.abs(qty) * type.sign;

  const position = useMemo(
    () => stock.find((s) => s.part_id === v.part_id),
    [stock, v.part_id],
  );
  const after = position && delta != null ? Number(position.qty_on_hand) + delta : null;

  const openBreakdowns = useMemo(
    () => (breakdowns ?? []).filter((b) => !['closed', 'cancelled'].includes(b.status)),
    [breakdowns],
  );

  function validate(): boolean {
    const e: Record<string, string> = {};
    if (!v.part_id) e.part_id = 'Choose the part';
    if (!v.movement_date) e.movement_date = 'Date is required';
    if (qty == null || (!isAdjustment && qty <= 0)) {
      e.qty = isAdjustment ? 'Give the correction, positive or negative' : 'Quantity must be more than zero';
    }
    if (isAdjustment && qty === 0) e.qty = 'An adjustment of zero changes nothing';
    if (after != null && after < 0) {
      e.qty = `This would take stock to ${num(after, 0)}. Receive stock or post an adjustment first.`;
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function submit() {
    if (!validate()) return;
    setBusy(true);
    const payload: Record<string, unknown> = { ...v, qty_delta: delta };
    if (record.id) payload.id = record.id;
    const ok = await save(
      () => upsert('part_stock_movements', payload as Record<string, unknown> & { id?: string }),
      'Stock movement posted',
    );
    setBusy(false);
    if (ok) onClose();
  }

  return (
    <Drawer
      open
      locked={locked}
      width={540}
      title={isNew ? 'Post a stock movement' : 'Stock movement'}
      subtitle="Every change to stock is a ledger entry, never an edited total"
      onClose={onClose}
      footer={
        <>
          {!isNew && (
            <ConfirmRemove
              disabled={busy}
              label="Reverse"
              question="Reverse this movement?"
              onConfirm={async () => {
                const ok = await save(() => softDelete('part_stock_movements', record.id as string), 'Reversed');
                // Close only if it went through. A refusal leaves the record
                // open with the reason on screen, rather than closing over it.
                if (ok !== null) onClose();
                return ok !== null;
              }}
            />
          )}
          <span className="spacer" />
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} loading={busy}>Post</Button>
        </>
      }
    >
      <div className="form-grid">
        <div className="field--full">
          <SelectInput id="mv-part" label="Part" required error={errors.part_id}
            value={(v.part_id as string) ?? null}
            options={(parts ?? []).map((p) => ({
              value: String(p.id), label: `${p.part_no} — ${p.description}`,
            }))}
            onChange={(x) => set('part_id', x)} />
        </div>

        <SelectInput id="mv-type" label="Movement" value={String(v.movement_type ?? 'receipt')}
          options={TYPES.map((t) => ({ value: t.value, label: t.label }))} placeholder=""
          onChange={(x) => set('movement_type', x ?? 'receipt')} />
        <TextInput id="mv-date" label="Date" type="date" required error={errors.movement_date}
          value={String(v.movement_date ?? '')} onChange={(x) => set('movement_date', x)} />

        <NumberInput id="mv-qty" label={isAdjustment ? 'Correction' : 'Quantity'}
          required error={errors.qty} step={1}
          min={isAdjustment ? undefined : 0}
          value={qty} onChange={setQty}
          help={isAdjustment
            ? 'Negative to write stock down, positive to write it up.'
            : type.sign < 0 ? 'Taken off the shelf.' : 'Added to the shelf.'} />
        <NumberInput id="mv-cost" label="Unit cost" min={0} step={10}
          value={(v.unit_cost as number) ?? null} onChange={(x) => set('unit_cost', x)} />

        {position && (
          <div className="field--full">
            <div className="issue issue--ok" style={{ alignItems: 'center' }}>
              <StatusChip tone={ragTone(position.status_colour)}>
                {num(position.qty_on_hand, 0)} now
              </StatusChip>
              <span className="spacer" />
              {after != null && (
                <span className={after < 0 ? 'small' : 'small muted'}
                  style={after < 0 ? { color: 'var(--rag-red)' } : undefined}>
                  {num(after, 0)} after this movement
                  {after < Number(position.min_level) && after >= 0 ? ' — below minimum' : ''}
                </span>
              )}
            </div>
          </div>
        )}

        <SelectInput id="mv-machine" label="Machine" value={(v.machine_id as string) ?? null}
          options={(machines ?? []).filter((m) => m.is_active)
            .map((m) => ({ value: m.id, label: m.fleet_no }))}
          onChange={(x) => set('machine_id', x)}
          help="Which machine consumed it — this is what makes cost per machine possible." />
        <SelectInput id="mv-bd" label="Breakdown" value={(v.breakdown_id as string) ?? null}
          options={openBreakdowns.map((b) => ({ value: b.id, label: b.breakdown_no }))}
          onChange={(x) => set('breakdown_id', x)} />

        <div className="field--full">
          <TextInput id="mv-ref" label="Reference" value={String(v.reference ?? '')}
            onChange={(x) => set('reference', x || null)}
            help="Delivery note, requisition or invoice number." />
        </div>
        <div className="field--full">
          <TextArea id="mv-notes" label="Notes" value={String(v.notes ?? '')}
            onChange={(x) => set('notes', x || null)} />
        </div>
      </div>
    </Drawer>
  );
}
